import React, { useEffect, useState, useRef } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Typography,
    Alert,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArchiveIcon from '@mui/icons-material/Archive';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArticleIcon from '@mui/icons-material/Article';
import { pdf } from '@react-pdf/renderer';
import { getContract, uploadContractPdf } from '../contracts.api';
import Fp1ContractPdf from './Fp1ContractPdf';
import {
    Fp1Contract,
    CONTRACT_STATUS_LABELS,
    CONTRACT_STATUS_COLORS,
    CONTRACT_DISCOUNT_TYPE_LABELS,
    Fp1ContractStatus,
} from '../contracts.types';
import Fp1ContractFormDialog from './Fp1ContractFormDialog';
import { Request } from 'api';

interface Props {
    open: boolean;
    contract: Fp1Contract | null;
    onClose: () => void;
    onSave: (updated: Fp1Contract) => void;
    onArchive: (uuid: string) => void;
    onDuplicate: (uuid: string) => void;
}

const formatDate = (d: string | null) =>
    d
        ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
          })
        : '--';

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box sx={{ display: 'flex', gap: 1, py: 0.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 170, flexShrink: 0 }}>
            {label}
        </Typography>
        <Typography variant="body2">{value ?? '--'}</Typography>
    </Box>
);

export default function Fp1ContractDetailDialog({
    open,
    contract: initialContract,
    onClose,
    onSave,
    onArchive,
    onDuplicate,
}: Props) {
    const [contract, setContract] = useState<Fp1Contract | null>(initialContract);
    const [loading, setLoading] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open || !initialContract) return;
        setContract(initialContract);
        setLoading(true);
        getContract(initialContract.uuid)
            .then((res) => setContract((res.data as any).data ?? res.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [open, initialContract]);

    const handleUploadClick = () => fileInputRef.current?.click();

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !contract) return;
        if (file.type !== 'application/pdf') {
            setUploadError('Only PDF files are allowed');
            return;
        }
        if (file.size > 20 * 1024 * 1024) {
            setUploadError('File must be under 20 MB');
            return;
        }
        setUploading(true);
        setUploadError(null);
        try {
            const res = await uploadContractPdf(contract.uuid, file);
            const updated = (res.data as any).data ?? res.data;
            setContract(updated);
        } catch {
            setUploadError('Upload failed. Please try again.');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const handleDownloadPdf = async () => {
        if (!contract?.signed_pdf_path) return;
        try {
            const res = await Request.get(`admin/fp1/contracts/${contract.uuid}/download-pdf`, {
                responseType: 'blob',
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${contract.contract_name}-signed.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            setUploadError('Download failed.');
        }
    };

    const handleGenerateContractPdf = async () => {
        if (!contract) return;
        setGeneratingPdf(true);
        try {
            const blob = await pdf(<Fp1ContractPdf contract={contract} />).toBlob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${contract.contract_name.replace(/\s+/g, '-')}-contract.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            setUploadError('PDF generation failed. Please try again.');
        } finally {
            setGeneratingPdf(false);
        }
    };

    if (!contract) return null;

    const expiryWarning = contract.is_expiring_within_30
        ? { severity: 'error' as const, msg: 'This contract expires within 30 days.' }
        : contract.is_expiring_within_60
        ? { severity: 'warning' as const, msg: 'This contract expires within 60 days.' }
        : null;

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
                            {contract.contract_name}
                        </Typography>
                        <Chip
                            label={CONTRACT_STATUS_LABELS[contract.status] ?? contract.status}
                            color={CONTRACT_STATUS_COLORS[contract.status] ?? 'default'}
                            size="small"
                            sx={{ fontWeight: 600 }}
                        />
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}

                    {!loading && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {expiryWarning && (
                                <Alert
                                    severity={expiryWarning.severity}
                                    icon={<WarningAmberIcon />}
                                >
                                    {expiryWarning.msg}
                                </Alert>
                            )}

                            {/* ── General ── */}
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Typography variant="overline" color="text.secondary">
                                    General
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <InfoRow label="Organization" value={contract.organization_name} />
                                <InfoRow label="Internal Notes" value={contract.internal_notes} />
                            </Paper>

                            {/* ── Dates ── */}
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Typography variant="overline" color="text.secondary">
                                    Dates
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <InfoRow
                                    label="Start Date"
                                    value={formatDate(contract.start_date)}
                                />
                                <InfoRow
                                    label="Expiration Date"
                                    value={formatDate(contract.expiration_date)}
                                />
                                <InfoRow
                                    label="Renewal Reminder"
                                    value={formatDate(contract.renewal_reminder_date)}
                                />
                                <InfoRow
                                    label="Auto Renew"
                                    value={contract.auto_renew ? 'Yes' : 'No'}
                                />
                            </Paper>

                            {/* ── Pricing ── */}
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Typography variant="overline" color="text.secondary">
                                    Pricing Terms
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <InfoRow
                                    label="Discount Type"
                                    value={
                                        contract.discount_type
                                            ? CONTRACT_DISCOUNT_TYPE_LABELS[contract.discount_type]
                                            : '--'
                                    }
                                />
                                <InfoRow
                                    label="Discount Value"
                                    value={contract.discount_value ?? '--'}
                                />
                            </Paper>

                            {/* ── Operational ── */}
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Typography variant="overline" color="text.secondary">
                                    Operational Terms
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <InfoRow label="Max Devices" value={contract.max_devices} />
                                {contract.custom_terms && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Custom Terms
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                mt: 0.5,
                                                p: 1.5,
                                                bgcolor: 'action.hover',
                                                borderRadius: 1,
                                                whiteSpace: 'pre-wrap',
                                            }}
                                        >
                                            {contract.custom_terms}
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>

                            {/* ── Documents ── */}
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Typography variant="overline" color="text.secondary">
                                    Documents
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                {uploadError && (
                                    <Alert severity="error" sx={{ mb: 1 }}>
                                        {uploadError}
                                    </Alert>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {contract.has_signed_pdf ? (
                                        <>
                                            <Chip
                                                label="PDF uploaded"
                                                color="success"
                                                size="small"
                                            />
                                            <Button
                                                size="small"
                                                startIcon={<DownloadIcon />}
                                                onClick={handleDownloadPdf}
                                            >
                                                Download PDF
                                            </Button>
                                        </>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No signed PDF uploaded yet
                                        </Typography>
                                    )}
                                    <Button
                                        size="small"
                                        startIcon={
                                            uploading ? (
                                                <CircularProgress size={14} />
                                            ) : (
                                                <UploadFileIcon />
                                            )
                                        }
                                        onClick={handleUploadClick}
                                        disabled={uploading}
                                        sx={{ ml: 'auto' }}
                                    >
                                        {contract.has_signed_pdf ? 'Replace PDF' : 'Upload PDF'}
                                    </Button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="application/pdf"
                                        hidden
                                        onChange={handleFileChange}
                                    />
                                </Box>
                            </Paper>

                            {/* ── Status History ── */}
                            {contract.status_history && contract.status_history.length > 0 && (
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Typography variant="overline" color="text.secondary">
                                        Status History
                                    </Typography>
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{ pl: 1 }}>
                                        {contract.status_history.map((entry) => (
                                            <Box
                                                key={entry.id}
                                                sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        bgcolor: 'primary.main',
                                                        mt: 0.75,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Box>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 0.75,
                                                            alignItems: 'center',
                                                            flexWrap: 'wrap',
                                                        }}
                                                    >
                                                        {entry.old_status && (
                                                            <>
                                                                <Chip
                                                                    label={
                                                                        CONTRACT_STATUS_LABELS[
                                                                            entry.old_status as Fp1ContractStatus
                                                                        ] ?? entry.old_status
                                                                    }
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ fontSize: '0.68rem' }}
                                                                />
                                                                <Typography variant="caption">
                                                                    →
                                                                </Typography>
                                                            </>
                                                        )}
                                                        <Chip
                                                            label={
                                                                CONTRACT_STATUS_LABELS[
                                                                    entry.new_status as Fp1ContractStatus
                                                                ] ?? entry.new_status
                                                            }
                                                            size="small"
                                                            sx={{ fontSize: '0.68rem' }}
                                                        />
                                                    </Box>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {entry.changed_by_name ?? 'System'} ·{' '}
                                                        {new Date(entry.changed_at).toLocaleString(
                                                            'en-US',
                                                            {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                            },
                                                        )}
                                                    </Typography>
                                                    {entry.notes && (
                                                        <Typography
                                                            variant="caption"
                                                            display="block"
                                                            color="text.secondary"
                                                        >
                                                            {entry.notes}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Paper>
                            )}

                            {/* ── Audit ── */}
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Typography variant="overline" color="text.secondary">
                                    Audit Info
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <InfoRow label="Created By" value={contract.created_by_name} />
                                <InfoRow
                                    label="Created At"
                                    value={new Date(contract.created_at).toLocaleString()}
                                />
                                <InfoRow
                                    label="Last Updated"
                                    value={new Date(contract.updated_at).toLocaleString()}
                                />
                                {contract.archived_at && (
                                    <InfoRow
                                        label="Archived At"
                                        value={new Date(contract.archived_at).toLocaleString()}
                                    />
                                )}
                            </Paper>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5, px: 2 }}>
                    <Button
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => onDuplicate(contract.uuid)}
                        color="inherit"
                    >
                        Duplicate
                    </Button>
                    {!contract.archived_at && (
                        <Button
                            size="small"
                            startIcon={<ArchiveIcon />}
                            onClick={() => onArchive(contract.uuid)}
                            color="warning"
                        >
                            Archive
                        </Button>
                    )}
                    <Button
                        size="small"
                        startIcon={generatingPdf ? <CircularProgress size={14} /> : <ArticleIcon />}
                        onClick={handleGenerateContractPdf}
                        disabled={generatingPdf}
                        color="primary"
                        variant="outlined"
                    >
                        {generatingPdf ? 'Generating…' : 'Download Contract PDF'}
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button onClick={onClose}>Close</Button>
                    <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() => setEditOpen(true)}
                    >
                        Edit
                    </Button>
                </DialogActions>
            </Dialog>

            <Fp1ContractFormDialog
                open={editOpen}
                contract={contract}
                onClose={() => setEditOpen(false)}
                onSave={(updated) => {
                    setEditOpen(false);
                    setContract(updated);
                    onSave(updated);
                }}
            />
        </>
    );
}
